import * as React from 'react';

import snakeCase from 'lodash-es/snakeCase';
import * as moment from 'moment';
import { useSelector } from 'react-redux';

import type User from '~/types/User';

import { countryHasMasterAccount } from '~/constants/countries';
import { getUserMetadataForSegment } from '~/selectors/user.selectors';
import { identify } from '~/utils/segment/index';
import { type Traits } from '~/utils/segment/schema';

/**
 * Formats an object to snake_case properties and date iso string
 * @param data
 */
const formatData = (data: Partial<User>): Traits => {
  if (!data) return data;

  /**
   * Moment object to ISO-8601
   * @param dateObject
   */
  const handleMoment = (dateObject: moment.Moment | undefined): string | undefined =>
    moment.isMoment(dateObject) ? (dateObject as moment.Moment).toISOString() : dateObject;

  /**
   * @param dateObject
   */
  const handleDate = (dateString: string) =>
    dateString ? new Date(dateString).toISOString() : dateString;

  try {
    return Object.keys(data).reduce((traits, key) => {
      const snakeKey = snakeCase(key);
      switch (key) {
        case 'address':
          if (!data.address) return traits;
          return {
            ...traits,
            sender_address_city: data.address.city,
            sender_address_company: data.address.company,
            sender_address_country_name: data.address.country?.name,
            sender_address_email: data.address.email,
            sender_address_name: data.address.name,
            sender_address_phone: data.address.phone,
            sender_address_state: data.address.state,
          };
        case 'currentPromo':
          if (!data.currentPromo) return traits;
          return {
            ...traits,
            current_promo_code: data.currentPromo?.code,
            current_promo_end_date: handleDate(data.currentPromo?.endDate),
            current_promo_start_date: handleDate(data.currentPromo?.startDate),
          };
        case 'dateJoined':
          return {
            ...traits,
            [snakeKey]: handleMoment(data[key]),
          };
        default:
          return {
            ...traits,
            [snakeKey]: data[key],
          };
      }
    }, {});
  } catch (error) {
    return {};
  }
};

/**
 * Monitors for user data changes and sets as Traits to Segment
 * @constructor
 */
const SegmentMonitor = () => {
  const [identified, setIdentified] = React.useState<boolean>(false);
  const user = useSelector(getUserMetadataForSegment);

  React.useEffect(() => {
    if (user && !identified) {
      const traits: Traits = {
        ...formatData(user),
        master_account_available: user.address?.country
          ? countryHasMasterAccount(user.address?.country)
          : undefined,
        orders_experience: 'new',
      };
      identify(user.id, traits as never);
      setIdentified(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  return null;
};

export default SegmentMonitor;
